import Alpine from 'alpinejs'
import focus from '@alpinejs/focus';
import { inView, animate, stagger, timeline } from "motion"
import '@tailwindplus/elements';
import 'lite-youtube-embed'
import '@slightlyoff/lite-vimeo';
import Swiper from 'swiper';
import {
    Pagination,
    A11y,
    Navigation,
    Autoplay,
} from 'swiper/modules';
import initCookieConsent from './components/cookieConsent.ts';

const startingWith = (subject, replacement) => (
  { name, value },
) => {
    if (name.startsWith(subject)) {
        // eslint-disable-next-line no-param-reassign
        name = name.replace(subject, replacement);
    }
    return { name, value };
};

Alpine.plugin(focus);
window.Alpine = Alpine;
document.addEventListener('alpine:init', () => {
    initCookieConsent();
});
Alpine.start();

// App main
const main = async () => {
    const lazysizes = await import(/* webpackChunkName: "lazysizes" */'lazysizes');
    return null;
};

const lightboxLoader = async () => {
    const { default: GLightbox }   = await import(/* webpackChunkName: "glightbox" */'glightbox');
    return GLightbox;
}

// LOAD lightbox
window.loadLightbox = async () => {
    const lightboxOnPage = document.querySelectorAll('.glightbox');

    if(lightboxOnPage.length > 0) {
        lightboxLoader().then( GLightbox => {
            const lightbox = GLightbox({
                touchNavigation: true,
                loop: true,
            });
        });
    }

    const lightboxVideoOnPage = document.querySelectorAll('.video-popup');

    if(lightboxVideoOnPage.length > 0) {
        lightboxLoader().then( GLightbox => {
            const lightboxVideo = GLightbox({
                selector: '.video-popup',
                touchNavigation: true,
                loop: false,
                autoplayVideos: true,
                autofocusVideos: true,
            });
        });
    }
}

const sliders =()=>{
    let sliders = document.querySelectorAll('[data-swiper]');

    sliders.forEach((slider, index) => {
        let slides = slider.querySelectorAll('.swiper-slide');
        let sliderData = JSON.parse(slider.dataset.swiper);

        let buttonNext = slider.querySelector('.button-next');
        let buttonPrev = slider.querySelector('.button-prev');

        const setSwiperButtonsTopPosition = function () {
          if (slider.querySelector('.swiper-buttons-container') && slider.querySelector('.image-container')) {
            const newTopPositionForButtonsContainer = slider.querySelector('.image-container').clientHeight / 2;
            slider.querySelector('.swiper-buttons-container').style.top = `${newTopPositionForButtonsContainer}px`;
          }
        }

        let defaultOptions = {
            modules: [Pagination, A11y, Navigation, Autoplay],
            slidesPerView: 1.2,
            spaceBetween: 32,
            initialSlide: 0,
            grabCursor: true,
            centeredSlides: false,
            loop: false,
            navigation: {
                nextEl: buttonNext,
                prevEl: buttonPrev,
                disabledClass: 'hidden',
            },
            pagination: {
                el: '.swiper-pagination',
                clickable: true
            },
            on: {
                init: setSwiperButtonsTopPosition,
                resize: setSwiperButtonsTopPosition,
              },
        }

        let typeOptions = {};
        switch(sliderData.type) {
            case 'quotes':
                typeOptions = {
                    centeredSlides: false,
                    spaceBetween: 16,
                    breakpoints: {
                        768: {
                            slidesPerView: 2,
                            centeredSlides: true,
                        },
                        1024: {
                            slidesPerView: 3,
                            centeredSlides: true,
                        },
                        1280: {
                            slidesPerView: 3,
                            centeredSlides: true,
                        },
                        1440: {
                            slidesPerView: 3.2,
                            centeredSlides: true,
                        },
                        1600: {
                            slidesPerView: 5,
                            centeredSlides: true,
                        },
                    },
                };
                break;
            case 'events':
                typeOptions = {
                    centeredSlides: false,
                    loop: false,
                    spaceBetween: 16,
                    breakpoints: {
                        768: {
                            slidesPerView: 2.2,
                        },
                        1024: {
                            slidesPerView: 2.6,
                            initialSlide: 2
                        },
                        1280: {
                            slidesPerView: 3,
                            initialSlide: 2,
                            centeredSlides: true,
                        },
                        1440: {
                            slidesPerView: 2.2,
                            initialSlide: 1,
                            centeredSlides: false,
                        },
                        1600: {
                            slidesPerView: 2.2,
                            initialSlide: 1,
                            centeredSlides: false,
                        },
                        1870: {
                            slidesPerView: 2.4,
                            initialSlide: 1,
                            centeredSlides: false,
                        },
                        2400: {
                            slidesPerView: 3.4,
                            initialSlide: 2,
                            centeredSlides: false,
                        },
                    },
                };
                break;
        }

        let options = {
            ...{
                ...defaultOptions,
                ...typeOptions
            },
            ...sliderData
        };

        /*
            For vertical sliders, just use the inline settings and ignore the presets stated above.
            We dynamically add the renderBullet stuff, and take the heading and description from the data attributes in templates/_partials/blocks/verticalSlider.twig
        */
        if (options.direction === 'vertical' && options.version === 'custom') {
            slides = slider.querySelectorAll('.swiper-slide');
            options = sliderData;
            options.modules = [Pagination, A11y, Navigation, Autoplay];
            options.pagination.renderBullet = function(index, className) {
                return (
                  '\
                  <div class="pag-container  ' +
                  className +
                  '">\
                  <div class="pag-text">' +
                  '<h3 class="leading-tight text-lg lg:text-xl font-bold mb-2">' + slides[index].dataset.heading + '</h3>' + '<p>' + slides[index].dataset.description + '</p>' +
                  "</div>\
                  </div>"
                );
              };
        }

        if (slides.length <= sliderData.minSlides) {
            options = {
                ...options,
                ...{
                    loop: false,
                    centeredSlides: false,
                }
            }
        }

        const swiper = new Swiper(slider, options);
    });
}

const setCorrectBackgroundElementsHeight =()=> {
    const elements = document.querySelectorAll('[data-backgroundelement]');
    elements.forEach((el) => {
        if (el.hasAttribute('data-backgroundelement') &&  el.querySelectorAll('.bg-point').length) {
            const backgroundElement = el.querySelector('.backgroundElement');
            const firstImageContainer = el.querySelectorAll('.bg-point')[0];
            const halfOfImageHeight = firstImageContainer.offsetHeight / 2;

            if (backgroundElement && backgroundElement.classList.contains('top-0')) {
                // console.log('Adjusting position of top background element');
                const totalHeight = firstImageContainer.getBoundingClientRect().top - el.getBoundingClientRect().top + halfOfImageHeight;
                backgroundElement.style.height = `${totalHeight}px`;
            }

            else if (backgroundElement && backgroundElement.classList.contains('bottom-0')) {
                // console.log('Adjusting position of bottom background element');
                const totalHeight = el.getBoundingClientRect().bottom - firstImageContainer.getBoundingClientRect().bottom + halfOfImageHeight;
                backgroundElement.style.height = `${totalHeight}px`;
            }
        }
    });
}

// Execute async function
main().then( (root) => {
    sliders();
    setTimeout(function() {
        setCorrectBackgroundElementsHeight();
    }, 1000);
    window.loadLightbox();

    window.addEventListener('resize', function() {
        setCorrectBackgroundElementsHeight();
    });

    // Hide all the placeholder images when lazyload image is unveiled (also works with AJAX loaded content)
    document.addEventListener('lazyloaded', function(e){
        const imageThatJustLoaded = e.target;
        if (imageThatJustLoaded.previousSibling && imageThatJustLoaded.previousSibling.classList.contains('placeholder')) {
            imageThatJustLoaded.previousSibling.classList.add('opacity-0');
        }
    });
});

const fadeSlideInUpViewport = document.querySelectorAll(".fadeSlideInUp-viewport");
if (fadeSlideInUpViewport.length) {
    inView(fadeSlideInUpViewport, ({ target }) => {
        const fadeSlideInUpsection = target.querySelectorAll(".fadeSlideInUp-section");
        if (fadeSlideInUpsection.length) {
            animate(
              fadeSlideInUpsection,
              { opacity: [0, 1], y: [50, 0], },
              { delay: 0.5, duration: 1, easing: [0.17, 0.55, 0.55, 1] }
            );
        }
        const fadeSlideInUpSectionHeaderCta = target.querySelectorAll(".fadeSlideInUp-section-header-cta");
        if (fadeSlideInUpSectionHeaderCta.length) {
            animate(
              fadeSlideInUpSectionHeaderCta,
              { opacity: [0, 1], y: [50, 0], },
              { delay: 1.3, duration: 1, easing: [0.17, 0.55, 0.55, 1] }
            );
        }
    });
}

const staggerClasses = document.querySelectorAll(".stagger");
if (staggerClasses.length) {
    inView(staggerClasses, ({ target }) => {
        const staggerItem = target.querySelectorAll(".stagger-item");
        if (staggerItem.length) {
            animate(
              staggerItem,
              { opacity: [0, 1] },
              { delay: stagger(0.3, { easing: "ease-out" }), duration: 1 }
            );
        }
    });
}

const timelineClasses = document.querySelectorAll(".timeline");
if (timelineClasses.length) {
    inView(timelineClasses, ({ target }) => {
        const timelineLeft = target.querySelectorAll(".timeline-left");
        if (timelineLeft.length) {
            animate(
              timelineLeft,
              { opacity: [0, 1], y: [50, 0] }, { duration: 0.6 },
            );
        }
        const timelineRight = target.querySelectorAll(".timeline-right");
        if (timelineRight.length) {
            animate(
              timelineRight,
              { opacity: [0, 1], x: [50, 0] }, { duration: 1.5, delay: 0.6 }
            );
        }
    });
}

const sliderFadeInLeftViewport = document.querySelectorAll(".slider-fadeInLeft-viewport");
if (sliderFadeInLeftViewport.length) {
    inView(sliderFadeInLeftViewport, ({ target }) => {
        const sliderFadeInLeftSection = target.querySelectorAll(".slider-fadeInLeft-section");
        if (sliderFadeInLeftSection.length) {
            animate(
              sliderFadeInLeftSection,
              { opacity: 1, x: [200, 0], },
              { delay: 0.5, duration: 1, easing: [0.17, 0.55, 0.55, 1] }
            );
        }
    });
}


// animate(
//     ".photo-animation",
//     { scale: [1, 1.03, 1]  },
//     {
//       duration: 15,
//       easing: "ease-in-out",
//       repeat: Infinity,
//     }
//   )

const draw = (progress) => ({
    strokeDashoffset: 1 - progress,
    visibility: "visible",
})

const timelineHeaderHome = document.querySelectorAll(".timeline-header-home");
if (timelineHeaderHome.length) {
    const sequenceCta = [
        [".path-header-home", draw(1), { duration: 1.6, delay: 0.8 }],
        [".header-home-play", { duration: 2.8, opacity: [0, 1] }],
    ]

    const sequenceCta2 = [
        [".path-header-home-mobile", draw(1), { duration: 1.6, delay: 0.8 }],
        [".header-home-play", { duration: 2.8, opacity: [0, 1] }],
    ]

    inView(timelineHeaderHome, ({ target }) => {
        timeline(sequenceCta, { delay: 0.5 })
        timeline(sequenceCta2, { delay: 0.5 })
    });
}

const ctaAlt = [
    [".path-cta-alt-left", draw(1), { duration: 1.6, delay: 0 }],
    [".path-cta-alt-right", draw(1), { duration: 2.5, at: "-1.0" }],
]

const ctaAltTimeline = document.querySelectorAll(".cta-alt-timeline");
if (ctaAltTimeline.length) {
    inView(ctaAltTimeline, ({ target }) => {
        timeline(ctaAlt, { delay: 0.1 })
    });
}


const sequenceDefault = [
    [".path-header-default", draw(1), { duration: 1.6, delay: 1.3 }],
]

const timelineHeaderDefault = document.querySelectorAll(".timeline-header-default");
if (timelineHeaderDefault.length) {
    inView(timelineHeaderDefault, ({ target }) => {
        timeline(sequenceDefault, { delay: 0.5 })
    });
}

const headerImageLeft = [
    [".path-header-image-left", draw(1), { duration: 1.5, delay: 1 }],
    [".path-header-image-left-teal", draw(1), { duration: 1.5, at: "-1" }],
]

const timelineHeaderImageLeft = document.querySelectorAll(".timeline-header-image-left");
if (timelineHeaderImageLeft.length) {
    inView(timelineHeaderImageLeft, ({ target }) => {
        timeline(headerImageLeft, { delay: 0.5 })
    });
}
